/**
 * Business Context Store
 * ======================
 * Global business memory that persists AI assistant profile settings.
 * Auto-fills niche, platform, audience, etc. in ALL tools.
 * 
 * This store syncs with the backend assistant settings on login.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/utils/api';

const initialBusinessContext = {
  // Core business identity
  niche: '',
  businessType: '',
  targetAudience: '',
  
  // Platform & content preferences
  primaryPlatform: 'tiktok',
  contentStyle: 'educational',
  
  // Brand voice & tone
  brandVoice: '',
  contentTone: '',
  
  // Offer focus
  offerFocus: '',
  
  // Loaded from backend
  configured: false,
  lastSyncedAt: null,
  
  // Loading state
  isLoading: false,
  error: null
};

export const useBusinessContext = create(
  persist(
    (set, get) => ({
      ...initialBusinessContext,
      
      // Sync with backend assistant settings
      syncFromBackend: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/ai-twin/settings');
          if (response.data) {
            set({
              niche: response.data.business_niche || '',
              targetAudience: response.data.target_audience || '',
              brandVoice: response.data.brand_voice || '',
              contentTone: response.data.content_tone || '',
              offerFocus: response.data.offer_focus || '',
              primaryPlatform: response.data.primary_platform || 'tiktok',
              configured: response.data.configured || false,
              lastSyncedAt: new Date().toISOString(),
              isLoading: false
            });
            return true;
          }
        } catch (error) {
          console.error('Failed to sync business context:', error);
          set({ isLoading: false, error: error.message });
        }
        return false;
      },
      
      // Save to backend assistant settings
      saveToBackend: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.put('/ai-twin/settings', {
            business_niche: data.niche || get().niche,
            target_audience: data.targetAudience || get().targetAudience,
            brand_voice: data.brandVoice || get().brandVoice,
            content_tone: data.contentTone || get().contentTone,
            offer_focus: data.offerFocus || get().offerFocus,
            primary_platform: data.primaryPlatform || get().primaryPlatform
          });
          
          if (response.data?.configured) {
            set({
              ...data,
              configured: true,
              lastSyncedAt: new Date().toISOString(),
              isLoading: false
            });
            return true;
          }
        } catch (error) {
          console.error('Failed to save business context:', error);
          set({ isLoading: false, error: error.message });
        }
        return false;
      },
      
      // Update local state (for form fields before saving)
      updateContext: (updates) => set(updates),
      
      // Set full context from wizard or settings
      setFullContext: (context) => set({
        niche: context.niche || context.business_niche || '',
        businessType: context.businessType || context.business_type || '',
        targetAudience: context.targetAudience || context.target_audience || '',
        primaryPlatform: context.primaryPlatform || context.platform || 'tiktok',
        brandVoice: context.brandVoice || context.brand_voice || '',
        contentTone: context.contentTone || context.content_tone || '',
        offerFocus: context.offerFocus || context.offer_focus || '',
        contentStyle: context.contentStyle || 'educational',
        configured: true,
        lastSyncedAt: new Date().toISOString()
      }),
      
      // Get context for API calls (formatted for backend)
      getContextForAPI: () => {
        const state = get();
        return {
          niche: state.niche,
          target_audience: state.targetAudience,
          platform: state.primaryPlatform,
          brand_voice: state.brandVoice,
          content_tone: state.contentTone,
          offer_focus: state.offerFocus
        };
      },
      
      // Get context for content generation
      getContentContext: () => {
        const state = get();
        return {
          niche: state.niche,
          audience: state.targetAudience,
          platform: state.primaryPlatform,
          style: state.contentStyle,
          tone: state.contentTone
        };
      },
      
      // Check if context is configured
      isConfigured: () => {
        const state = get();
        return state.configured && !!state.niche;
      },
      
      // Reset context
      resetContext: () => set(initialBusinessContext)
    }),
    {
      name: 'paygbot-business-context',
      version: 1
    }
  )
);

export default useBusinessContext;
